// /weighbridge/settings/print-templates — Phase C12 / post-C13 cleanup.
//
// `PrintTemplateType` (clients/web/src/types/api.ts) now includes the four
// WB template types (`wb_ticket`, `wb_advice_note`, `wb_wtn`,
// `wb_hazardous_consignment`); `PrintTemplatesListPage` knows how to
// render their labels. The generic list page does not currently accept a
// module filter, so this page is a deep-link navigational surface plus a
// per-type quick-edit table for the four WB templates.

import { Link } from 'react-router-dom';
import { Pencil } from 'lucide-react';

import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import type { PrintTemplateType } from '../../../types/api';

interface WbPrintTemplateRow {
  readonly type: Extract<
    PrintTemplateType,
    'wb_ticket' | 'wb_advice_note' | 'wb_wtn' | 'wb_hazardous_consignment'
  >;
  readonly label: string;
  readonly description: string;
}

const WB_TEMPLATES: ReadonlyArray<WbPrintTemplateRow> = [
  {
    type: 'wb_ticket',
    label: 'Weighbridge Ticket',
    description:
      'Headline ticket PDF rendered from each completed weighbridge job.',
  },
  {
    type: 'wb_advice_note',
    label: 'Advice Note',
    description:
      'Internal advice / dispatch note emailed to the customer at ticket completion.',
  },
  {
    type: 'wb_wtn',
    label: 'Waste Transfer Note',
    description:
      'Produced when WTN compliance is enabled and the ticket carries waste lines.',
  },
  {
    type: 'wb_hazardous_consignment',
    label: 'Hazardous Consignment Note',
    description:
      'Produced when hazardous compliance is enabled and the ticket carries hazardous lines.',
  },
];

export function WbPrintTemplatesPage(): JSX.Element {
  return (
    <div>
      <PageHeader
        title="Weighbridge Print Templates"
        subtitle="Manage the layouts for ticket, advice note, WTN, and hazardous consignment PDFs."
      />
      <Card title="Weighbridge templates">
        <p>
          Each template type below has a working default seeded for new
          tenants. Edit any of them to override the seed for your
          organisation.
        </p>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {WB_TEMPLATES.map((row) => (
            <li
              key={row.type}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '1rem',
                padding: '0.75rem 0',
                borderTop: '1px solid var(--color-border, #e5e7eb)',
              }}
              data-testid={`wb-template-row-${row.type}`}
            >
              <div>
                <h4 style={{ margin: 0 }}>{row.label}</h4>
                <p style={{ margin: '0.25rem 0 0', color: 'var(--color-muted)' }}>
                  {row.description}
                </p>
              </div>
              <Link
                to={`/settings/print-templates/${row.type}/edit`}
                style={{ textDecoration: 'none' }}
              >
                <Button variant="primary" icon={Pencil}>
                  Edit
                </Button>
              </Link>
            </li>
          ))}
        </ul>
        <p style={{ marginTop: '1rem' }}>
          <Link to="/settings/print-templates">
            Open the global print templates list →
          </Link>
        </p>
      </Card>
    </div>
  );
}
